// Shared labels/helpers for the student subscription pages. Plain exports (no
// reactivity) so any view can import what it needs — keeps Arabic strings and
// formatting in one place (no duplication across pages).

export const PLAN_TYPES = ['monthly', 'term1', 'term2', 'yearly'];

export const PLAN_LABELS = {
  monthly: 'اشتراك شهري',
  term1: 'الترم الأول',
  term2: 'الترم الثاني',
  yearly: 'اشتراك سنوي',
  // Courses: one-time purchase, permanent access (no expiry).
  lifetime: 'مدى الحياة',
};

export const STATUS_LABELS = {
  pending: 'بانتظار الدفع',
  waiting_payment: 'قيد مراجعة الدفع',
  active: 'مفعّل',
  expired: 'منتهي',
  cancelled: 'ملغي',
};

export const STATUS_TONE = {
  pending: 'gray',
  waiting_payment: 'amber',
  active: 'green',
  expired: 'red',
  cancelled: 'dark',
};

export const REQUEST_STATUS_LABELS = {
  pending: 'قيد المراجعة',
  approved: 'مقبول',
  rejected: 'مرفوض',
};

export const REQUEST_STATUS_TONE = {
  pending: 'amber',
  approved: 'green',
  rejected: 'red',
};

export const NOTIFICATION_ICON = {
  payment_received: 'pi-inbox',
  payment_approved: 'pi-check-circle',
  payment_rejected: 'pi-times-circle',
  subscription_activated: 'pi-verified',
  subscription_expiring: 'pi-clock',
  subscription_expired: 'pi-exclamation-triangle',
};

// Dual/singular Arabic day-count grammar ("1 يوم" / "2 أيام" both read wrong in
// Arabic) — mirrors the backend's arabic_days_phrase() used in reminder text.
export function daysRemainingPhrase(days) {
  if (days === null || days === undefined) return '—';
  if (days === 1) return 'متبقي يوم واحد';
  if (days === 2) return 'متبقي يومين';
  return `متبقي ${days} يوم`;
}

// Time-based badge for the 3 states the raw `status` enum can't express on
// its own (expired/expires_today/expiring_soon) — driven ENTIRELY by the
// backend-computed `expiry_state` field (Subscription::expiryState()), never
// re-derived from status+days_remaining here. The backend is the single
// source of truth: it already reconciles "expired" the same way content-
// access authorization does, so the UI can never show "سينتهي قريباً" for a
// subscription that's actually expired (see Subscription::expiryState()'s
// docblock for the bug this replaced).
export const EXPIRY_STATE_BADGE = {
  expired: { text: '🔴 الاشتراك منتهي', tone: 'red' },
  expires_today: { text: '🟠 الاشتراك ينتهي اليوم', tone: 'amber' },
  expiring_soon: { text: '🟠 اشتراكك سينتهي قريباً', tone: 'amber' },
};

// Returns {text, tone} for expired/expires_today/expiring_soon, or null for
// every other expiry_state (active, or a non-time-based status like pending/
// waiting_payment/cancelled) — callers fall back to statusLabel/statusTone.
export function expiryStateBadge(s) {
  return (s && EXPIRY_STATE_BADGE[s.expiry_state]) || null;
}

// Does this subscription's real, backend-computed state warrant the
// "expiring soon" treatment? Never true for an actually-expired subscription.
export function isExpiringSoon(s) {
  return !!s && s.expiry_state === 'expiring_soon';
}

export function isExpiredState(s) {
  return !!s && s.expiry_state === 'expired';
}

export function isExpiresToday(s) {
  return !!s && s.expiry_state === 'expires_today';
}

export function planLabel(t) { return PLAN_LABELS[t] ?? t; }
export function statusLabel(s) { return STATUS_LABELS[s] ?? s; }
export function statusTone(s) { return STATUS_TONE[s] ?? 'gray'; }
export function requestStatusLabel(s) { return REQUEST_STATUS_LABELS[s] ?? s; }
export function requestStatusTone(s) { return REQUEST_STATUS_TONE[s] ?? 'gray'; }
export function notificationIcon(t) { return NOTIFICATION_ICON[t] ?? 'pi-bell'; }

// Canonicalises a subscribable "type" coming from the URL / backend deny-response
// into the two values the subscribe API expects: 'academic_year' | 'course'.
// Tolerates every academic-year spelling seen in the wild — 'academicyear'
// (class_basename), 'academic-year', 'academic_year', 'AcademicYear' — so the
// /subscribe flow never mis-routes an academic year through the course API.
export function normalizeSubscribableType(type) {
  const t = String(type || '').toLowerCase().replace(/[-_]/g, '');
  return t === 'academicyear' ? 'academic_year' : 'course';
}

export function typeLabel(type) {
  if (!type) return '';
  const base = String(type).split('\\').pop();
  return base === 'Course' || base === 'course' ? 'كورس'
    : base === 'AcademicYear' || base === 'academic_year' ? 'سنة دراسية' : base;
}

export function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('en-EG') + ' ج.م';
}

export function formatDate(value) {
  if (!value) return '—';
  const d = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' });
}

// Available plans for a product record (year or course) from its price columns.
export function productPlans(product) {
  if (!product) return [];
  if (Array.isArray(product.plans) && product.plans.length) {
    return product.plans.map(p => ({ type: p.type, label: planLabel(p.type), price: Number(p.price) }));
  }
  return PLAN_TYPES
    .map(t => ({ type: t, label: planLabel(t), price: product[`${t}_price`] }))
    .filter(p => p.price !== null && p.price !== undefined && p.price !== '')
    .map(p => ({ ...p, price: Number(p.price) }));
}

export function apiError(error, fallback = 'حدث خطأ، برجاء المحاولة مرة أخرى') {
  return error?.response?.data?.message || fallback;
}

// The card's "starts from" price — prefers the backend-computed starting_price
// (Course/AcademicYear payloads already include it), falling back to the
// minimum of the plans array so nothing is ever hardcoded on the frontend.
export function startingPriceOf(product) {
  if (!product) return null;
  if (product.starting_price !== undefined && product.starting_price !== null) {
    return Number(product.starting_price);
  }
  const prices = productPlans(product).map(p => p.price);
  return prices.length ? Math.min(...prices) : null;
}
